import { PageHero, Reveal } from "@/components/Shared";

const sections = [
    {
        title: "1. Scope of Services",
        paragraphs: [
            "Infrasic Intelligence provides specialised professional services relating to forensic investigation, financial intelligence, financial analysis, digital forensics, digital-data review, fraud investigation, asset tracing, due diligence, investigative intelligence and related analytical support.",
            "The specific nature, scope, deliverables, responsibilities, fees and other terms applicable to any professional engagement will be governed by the relevant engagement letter, proposal, statement of work, agreement or other mutually accepted contractual arrangement.",
            "Information published on this website is provided for general informational purposes only.",
            "Nothing on this website constitutes legal advice, regulatory advice, accounting advice, audit opinion, investigation opinion, professional advice or any other advice in relation to a specific matter.",
            "No professional engagement is created merely by accessing this website or communicating through the website.",
        ],
    },
    {
        title: "2. Professional Engagements",
        paragraphs: [
            "Any professional services provided by Infrasic Intelligence are subject to the terms agreed for the relevant engagement.",
            "The scope and conclusions of an engagement may depend upon the information, records, evidence, instructions, access and circumstances available to us at the relevant time.",
            "We do not guarantee any particular outcome, finding, recovery, enforcement action, litigation result or other result from an investigation or professional engagement.",
        ],
    },
    {
        title: "3. Confidentiality and Professional Care",
        paragraphs: [
            "We endeavour to handle information received in connection with professional matters with appropriate discretion, care and confidentiality.",
            "However, confidentiality may be subject to:",
            "• Applicable laws and regulations;\n• Court orders or lawful directions;\n• Regulatory or governmental requirements;\n• Contractual obligations;\n• Professional obligations; and\n• Circumstances in which disclosure is legally required or permitted.",
            "Where a formal engagement exists, confidentiality and information-handling obligations will be governed by the applicable engagement documentation and law.",
        ],
    },
    {
        title: "4. Website Use",
        paragraphs: [
            "You may use this website only for lawful purposes.",
            "You must not:",
            "• Attempt to gain unauthorised access to the website or its systems;\n• Interfere with or disrupt website functionality;\n• Introduce malicious code, malware or other harmful material;\n• Attempt to compromise the security or availability of the website;\n• Misuse or misrepresent information obtained from the website;\n• Copy or exploit website content for unauthorised commercial purposes; or\n• Use the website in a manner that violates applicable law.",
            "We reserve the right to restrict or suspend access where reasonably necessary to protect the website, our systems, our users or our legitimate interests.",
        ],
    },
    {
        title: "5. Website Information",
        paragraphs: [
            "We make reasonable efforts to maintain accurate and current information on the website.",
            "However, website content may be updated, modified, supplemented or removed from time to time without prior notice.",
            "Information published on the website should not be treated as a substitute for obtaining professional advice or conducting appropriate independent verification in relation to a specific matter.",
            "We do not guarantee that the website will always be available, uninterrupted, completely accurate, current or free from technical errors.",
        ],
    },
    {
        title: "6. Intellectual Property",
        paragraphs: [
            "Unless otherwise stated, the content and materials appearing on this website, including text, graphics, design elements, branding, logos, photographs, illustrations, layouts and other materials, are owned by Infrasic Intelligence or are used with appropriate authorisation.",
            "Nothing on this website grants you any licence or right to reproduce, modify, republish, distribute, transmit, commercially exploit or otherwise use our intellectual property without prior written permission, except where such use is expressly permitted by applicable law.",
            "The name Infrasic Intelligence, associated branding and other proprietary materials may not be used in a manner that suggests an unauthorised association, endorsement or affiliation.",
        ],
    },
    {
        title: "7. Third-Party Links",
        paragraphs: [
            "The website may contain links to third-party websites, platforms or resources.",
            "Such links are provided for convenience or informational purposes only.",
            "We do not control, endorse or assume responsibility for the content, availability, security, privacy practices or terms of any third-party website.",
            "Access to third-party websites is at your own discretion and subject to their applicable terms and policies.",
        ],
    },
    {
        title: "8. Limitation of Liability",
        paragraphs: [
            "To the maximum extent permitted by applicable law, Infrasic Intelligence shall not be liable for any indirect, incidental, special, consequential or exemplary loss or damage arising from or relating to your access to or use of this website, including loss of business, revenue, opportunity, data or goodwill.",
            "Nothing in these Terms is intended to exclude or limit liability that cannot lawfully be excluded or limited under applicable law.",
            "Any liability arising from professional services provided by Infrasic Intelligence shall be governed by the terms of the relevant engagement and applicable law.",
        ],
    },
    {
        title: "9. Indemnity",
        paragraphs: [
            "To the extent permitted by applicable law, you agree to use the website lawfully and not to cause harm to the website, its systems or Infrasic Intelligence through misuse of the website or violation of these Terms.",
            "Nothing in this clause limits any rights or remedies available to Infrasic Intelligence under applicable law.",
        ],
    },
    {
        title: "10. Changes to these Terms",
        paragraphs: [
            "We may modify these Terms of Use from time to time to reflect changes in our services, website, technology, legal requirements or business practices.",
            "The updated version will be published on this page with the revised “Last Updated” date.",
            "Your continued use of the website after an updated version is published constitutes use of the website subject to the revised Terms, to the extent permitted by applicable law.",
        ],
    },
    {
        title: "11. Governing Law and Jurisdiction",
        paragraphs: [
            "These Terms are governed by and construed in accordance with the laws of India.",
            "Subject to applicable law, courts having jurisdiction in Mumbai, Maharashtra, India shall have jurisdiction over disputes arising out of or relating to these Terms or your use of this website.",
        ],
    },
    {
        title: "12. Contact",
        paragraphs: [
            "For questions regarding these Terms of Use, please contact:",
            "INFRASIC INTELLIGENCE (OPC) PRIVATE LIMITED\nC Wing, 202, Nikita Apartment II,\nShimpoli, Borivali West, Mumbai – 400092,\nMaharashtra, India",
            "Email: contactus@infrasic.in\nPhone: +91 76111 81818",
        ],
    },
];

export default function Terms() {
    return (
        <div data-testid="terms-page">
            <PageHero
                overline="Terms"
                title="Terms of Use"
                lead="Last Updated: 30 September 2026"
                testId="terms-hero"
            />

            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10">
                <div className="space-y-8">
                    <Reveal>
                        <div className="border border-[#243650] bg-[#0F1B2E] p-8 lg:p-10" data-testid="terms-intro">
                            <p className="text-base leading-8 text-[#9AA7BF]">
                                These Terms of Use govern access to and use of the website of Infrasic Intelligence (OPC) Private Limited (“Infrasic Intelligence”, “we”, “us” or “our”).
                            </p>
                            <p className="mt-4 text-base leading-8 text-[#9AA7BF]">
                                By accessing or using this website, you acknowledge these Terms of Use.
                            </p>
                        </div>
                    </Reveal>

                    {sections.map((section, index) => (
                        <Reveal key={section.title} delay={index * 0.04}>
                            <article className="border border-[#243650] bg-[#0F1B2E] p-8 lg:p-10" data-testid={`terms-section-${index}`}>
                                <h2 className="font-display text-2xl font-semibold text-[#E8ECF4]">{section.title}</h2>
                                <div className="mt-6 space-y-4 text-base leading-8 text-[#9AA7BF]">
                                    {section.paragraphs.map((paragraph) => (
                                        <p key={paragraph} className="whitespace-pre-line">{paragraph}</p>
                                    ))}
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
